import { useMemo } from 'react'
import html from './template/Home.html?raw'
import { rewriteTemplateHtml } from '@/lib/templateHtml'
import { AppointmentForm } from '@/components/website/AppointmentForm'
import { PricingPlans } from '@/components/website/PricingPlans'

/**
 * The homepage body is authored as the hospital template. Two sections are
 * carved out of the raw markup and replaced with live React components so the
 * dashboard can manage them:
 *  - the appointment section ships a static, non-functional form
 *  - the pricing section ships hard-coded plans, so it is replaced with the
 *    dashboard-managed <PricingPlans /> component
 * The template's own markup is preserved elsewhere.
 */
const APPOINTMENT_SECTION_OPEN = '<section class="appointment-section-3">'
const APPOINTMENT_SECTION_CLOSE = '</section>'
const PRICING_SECTION_OPEN = '<section class="pricing-section-1'
const PRICING_SECTION_CLOSE = '</section>'

function splitAroundSection(
  raw: string,
  openMarker: string,
  closeMarker: string,
): { before: string; after: string } {
  const start = raw.indexOf(openMarker)
  if (start === -1) return { before: raw, after: '' }
  const end = raw.indexOf(closeMarker, start)
  if (end === -1) return { before: raw, after: '' }
  return {
    before: raw.slice(0, start),
    after: raw.slice(end + closeMarker.length),
  }
}

export function HomeThree() {
  const { before, after } = useMemo(
    () =>
      splitAroundSection(
        rewriteTemplateHtml(html),
        APPOINTMENT_SECTION_OPEN,
        APPOINTMENT_SECTION_CLOSE,
      ),
    [],
  )

  // The pricing section lives after the appointment section in the template.
  const { before: beforePricing, after: afterPricing } = useMemo(
    () => splitAroundSection(after, PRICING_SECTION_OPEN, PRICING_SECTION_CLOSE),
    [after],
  )

  // React compares `dangerouslySetInnerHTML` by reference, so the `{ __html }`
  // objects must be memoised. Otherwise every HomeThree re-render (e.g. when a
  // background query settles) re-assigns innerHTML and wipes the background
  // images, hero/testimonial sliders, counters and video popup bindings that
  // the template initializer applied.
  const beforeHtml = useMemo(() => ({ __html: before }), [before])
  const beforePricingHtml = useMemo(() => ({ __html: beforePricing }), [beforePricing])
  const afterPricingHtml = useMemo(() => ({ __html: afterPricing }), [afterPricing])

  return (
    <>
      <div className="template-page" dangerouslySetInnerHTML={beforeHtml} />

      {/* appointment section start (live form) */}
      <section className="appointment-section-3">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="appointment-box">
                <div className="appointment-content wow fadeInUp" data-wow-delay=".2s">
                  <div className="section-title">
                    <span className="sub-title">Appointment</span>
                    <h2>Book an Appointment for Expert Consultation</h2>
                    <p>
                      Book an appointment with our experienced doctors for emergency care, general medicine,
                      cardiology, orthopedics, pediatrics and diagnostics. Choose your department, doctor, date
                      and time — we will confirm your serial by SMS.
                    </p>
                  </div>
                  <div className="appointment-image">
                    <figure className="image-anime">
                      <img
                        src="/assets/images/appointment/appointment-3-1.jpg"
                        alt="appointment image"
                      />
                    </figure>
                  </div>
                </div>
                <div className="appointment-form-box wow fadeInUp" data-wow-delay=".3s">
                  <div className="default-form appointment-form">
                    <AppointmentForm idPrefix="home-appt" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* appointment section end */}

      <div className="template-page" dangerouslySetInnerHTML={beforePricingHtml} />

      {/* pricing section start (live plans) */}
      <PricingPlans />
      {/* pricing section end */}

      <div className="template-page" dangerouslySetInnerHTML={afterPricingHtml} />
    </>
  )
}
